/**
 * BL-washbasin-selector-v2.js
 */

(function () {
  'use strict';

  function getCartAddonInterceptorRegistry() {
    if (!window.__blCartAddonInterceptor) {
      window.__blCartAddonInterceptor = {
        installed: false,
        originalFetch: window.fetch.bind(window),
        providers: new Map()
      };
    }
    return window.__blCartAddonInterceptor;
  }

  function installCartAddonFetchInterceptor() {
    const registry = getCartAddonInterceptorRegistry();
    if (registry.installed) return registry;

    registry.installed = true;

    window.fetch = function(url, config) {
      const requestUrl = typeof url === 'string' ? url : url?.url || '';

      if (!requestUrl.includes('/cart/add') || !config) {
        return registry.originalFetch.call(this, url, config);
      }

      const providers = Array.from(registry.providers.values()).sort((a, b) => (b.priority || 0) - (a.priority || 0));

      for (const provider of providers) {
        if (!provider || typeof provider.getBundlePayload !== 'function') continue;

        const payload = provider.getBundlePayload(config);
        if (!payload || !Array.isArray(payload.items) || !payload.items.length) continue;

        const newConfig = Object.assign({}, config);
        newConfig.headers = Object.assign({}, config.headers || {}, {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        });
        newConfig.body = JSON.stringify({
          items: payload.items,
          sections: payload.sections || ''
        });

        const responsePromise = registry.originalFetch.call(this, url, newConfig);

        if (typeof provider.onSuccess === 'function') {
          return responsePromise.then(response => {
            if (response.ok) provider.onSuccess(response);
            return response;
          });
        }

        return responsePromise;
      }

      return registry.originalFetch.call(this, url, config);
    };

    return registry;
  }

  function buildStableBundleId(mainVariantId, linkedVariantIds) {
    const mainId = String(mainVariantId || '').replace(/[^a-zA-Z0-9_-]/g, '');
    const linkedIds = (Array.isArray(linkedVariantIds) ? linkedVariantIds : [linkedVariantIds])
      .map((id) => String(id || '').replace(/[^a-zA-Z0-9_-]/g, ''))
      .filter(Boolean)
      .sort();

    if (!mainId || !linkedIds.length) return `wb_${Date.now()}`;
    return `wb_${mainId}_${linkedIds.join('_')}`;
  }

  function getWashbasinSelectionRegistry() {
    if (!window.__blWashbasinSelectionRegistry) {
      window.__blWashbasinSelectionRegistry = new Map();
    }
    return window.__blWashbasinSelectionRegistry;
  }

  function getProductAddonStateStore() {
    if (!window.__blProductAddonsStateStore) {
      window.__blProductAddonsStateStore = {
        selections: new Map()
      };
    }
    return window.__blProductAddonsStateStore;
  }

  function getSelectionStorage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function getUrlSelectedBasin() {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('bl_wb');
    } catch (error) {
      return null;
    }
  }

  class WashbasinSelectorV2 {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this._fetchProviderId = `washbasin-v2:${this.sectionId}:${Math.random().toString(36).slice(2)}`;
      const configEl = container.querySelector('[data-washbasin-config]');
      this.config = configEl ? JSON.parse(configEl.textContent) : {};
      
      this.slider = container.querySelector('[data-washbasin-slider]');
      this.pillRow = container.querySelector('[data-pill-row]');
      this.pills = container.querySelectorAll('[data-pill-value]');
      this.items = container.querySelectorAll('.washbasin-selector__item');
      this.inputs = container.querySelectorAll('.washbasin-selector__input');
      
      this.currentWidth = this.config.currentWidth || null;
      this.lastSelectedBasinId = null;
      this._isInternalScroll = false;
      this._isUpdating = false;

      this.init();
    }

    getSelectionMemoryKey() {
      const scope = this.container.dataset.selectionScope || this.config.selectionScope || this.sectionId;
      return `washbasin-v2:${scope}`;
    }

    loadRememberedBasin() {
      // In-memory registry only.  sessionStorage is intentionally not used:
      // the Map lives for the lifetime of the current JS context, so basin
      // selections survive AJAX swaps within the same page session but are
      // discarded on full-page navigation or refresh.  Persisting to
      // sessionStorage caused a price flash when the user navigated away and
      // came back — the page painted the native (no-addon) price first, then
      // JS rehydrated the stored basin and updated the price, producing a
      // visible flicker.
      const key = this.getSelectionMemoryKey();
      return getWashbasinSelectionRegistry().get(key) || null;
    }

    rememberBasinSelection(value) {
      // Write to the in-memory registry only — see loadRememberedBasin for
      // the rationale behind not using sessionStorage here.
      const registry = getWashbasinSelectionRegistry();
      const key = this.getSelectionMemoryKey();
      if (value) {
        registry.set(key, value);
      } else {
        registry.delete(key);
      }
    }

    init() {
      this.bindPillEvents();
      this.bindSliderEvents();
      this.bindGlobalEvents();

      this.currentMainVariantId = this.getCurrentMainVariantId();
      const urlSelectedBasin = getUrlSelectedBasin();
      const rememberedBasinId = this.loadRememberedBasin();
      const def = this.container.dataset.defaultSelection || 'none';

      if (urlSelectedBasin === 'none' && this.container.querySelector('input[value="none"]')) {
        this.selectValue('none', true, false);
      } else if (urlSelectedBasin && this.container.querySelector(`input[value="${urlSelectedBasin}"]`)) {
        this.selectValue(urlSelectedBasin, true, false);
      } else if (rememberedBasinId && this.container.querySelector(`input[value="${rememberedBasinId}"]`)) {
        this.selectValue(rememberedBasinId, true);
      } else if (def === 'none' || this.container.querySelector(`input[value="${def}"]`)) {
        this.selectValue(def, true);
      }

      this.handlePossibleWidthChange(true);
      if (def === 'basin' && !this.getSelectedValue()) {
        this.ensureValidSelection(null, true);
      }
      this.injectPriceOverrideCSS();
      this.interceptFetch();
      this.initDynamicCheckoutBridge();
      this.scheduleGlobalPriceUpdate();
    }

    getProductForm() {
      const root = this.getMainProductRoot();
      return root?.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
             root?.querySelector('form[action*="/cart/add"]') ||
             document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
             document.querySelector('form[action*="/cart/add"]');
    }

    initDynamicCheckoutBridge() {
      this._boundDynamicCheckoutClick = (event) => {
        const button = event.target.closest('.shopify-payment-button__button--unbranded');
        if (!button) return;

        const form = this.getProductForm();
        if (!form || !button.closest('form') || button.closest('form') !== form) return;
        if (this._dynamicCheckoutPending) return;

        const payload = this.buildBundlePayload({ body: new FormData(form) });
        if (!payload || !Array.isArray(payload.items) || payload.items.length < 2) return;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this._dynamicCheckoutPending = true;
        button.setAttribute('disabled', 'disabled');
        button.classList.add('is-loading');

        const registry = getCartAddonInterceptorRegistry();
        registry.originalFetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: payload.items })
        })
          .then(async (response) => {
            if (!response.ok) {
              let message = 'Dynamic checkout bundle add failed';
              try {
                const error = await response.json();
                message = error?.description || error?.message || message;
              } catch (error) {}
              throw new Error(message);
            }

            window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
          })
          .catch((error) => {
            console.error('[BL-Washbasin-V2] Dynamic checkout bridge failed', error);
            button.removeAttribute('disabled');
            button.classList.remove('is-loading');
            this._dynamicCheckoutPending = false;
          });
      };

      document.addEventListener('click', this._boundDynamicCheckoutClick, true);
    }

    injectPriceOverrideCSS() {
        window.BLBundlePriceState?.ensureCSS?.();
    }

    bindPillEvents() {
      this.pills.forEach(pill => {
        pill.addEventListener('click', () => {
          const val = pill.dataset.pillValue;
          this.selectValue(val);
        });
      });
    }

    bindSliderEvents() {
      if (!this.slider) return;

      // Handle direct clicks on cards/labels explicitly instead of relying only
      // on the hidden radio change event. This prevents the scroll-sync logic
      // from overriding a user click when the card is not currently centered.
      this.container.addEventListener('click', (e) => {
        const item = e.target.closest('.washbasin-selector__item[data-washbasin-id]');
        if (!item || !this.container.contains(item)) return;

        const value = item.dataset.washbasinId;
        if (!value) return;

        this._suspendCenterSyncUntil = Date.now() + 800;
        this.selectValue(value);
      });

      // Keep the radio path as a fallback for keyboard/input-driven selection.
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('.washbasin-selector__input')) {
          this._suspendCenterSyncUntil = Date.now() + 800;
          this.selectValue(e.target.value);
        }
      });

      // Sync active pill when scrolling
      this.slider.addEventListener('scroll', () => {
        this.updateProgressBar();
        if (this._isInternalScroll) return;
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(() => {
          if (this._suspendCenterSyncUntil && Date.now() < this._suspendCenterSyncUntil) return;
          this.syncPillToVisibleCard();
        }, 150);
      }, { passive: true });
      
      // Initialize progress bar
      this.updateProgressBar();

      // Arrows
      const prev = this.container.querySelector('[data-washbasin-prev]');
      const next = this.container.querySelector('[data-washbasin-next]');
      if (prev) prev.addEventListener('click', () => this.scrollSlider('prev'));
      if (next) next.addEventListener('click', () => this.scrollSlider('next'));
    }

    bindGlobalEvents() {
      const isSyncSuspended = () => this._suspendSyncUntil && Date.now() < this._suspendSyncUntil;

      this._boundVariantChange = (event) => {
          if (isSyncSuspended()) return;

          const variant = event.detail && event.detail.variant;
          if (!variant) return;

          this.currentMainVariantId = variant.id;
          const width = this.extractWidthFromVariant(variant);
          if (width) {
              this.updateState(width);
          }
      };

      this._boundVariantChanged = (event) => {
          if (isSyncSuspended()) return;

          if (event.detail && event.detail.variant) {
              this.currentMainVariantId = event.detail.variant.id;
              const width = this.extractWidthFromVariant(event.detail.variant);
              if (width) {
                  this.updateState(width);
                  return;
              }
          }

          this.currentMainVariantId = this.getCurrentMainVariantId();
          this.handlePossibleWidthChange(true);
      };

      this._boundGroupingSwapped = () => {
          if (isSyncSuspended()) return;

          // When the instance was preserved through an AJAX swap,
          // restorePersistentWashbasinBlock already did the update inline
          // (without scroll). Skip to avoid re-triggering scrollToValue.
          if (this._skipGroupingSwapped) {
            this._skipGroupingSwapped = false;
            return;
          }
          this.refreshVariantBindings();
          this.currentMainVariantId = this.getCurrentMainVariantId();
          this.handlePossibleWidthChange(true);
      };

      this._boundPickerInteraction = () => {
          if (isSyncSuspended()) return;

          clearTimeout(this._pickerInteractionTimer);
          this._pickerInteractionTimer = setTimeout(() => {
              if (isSyncSuspended()) return;
              this.currentMainVariantId = this.getCurrentMainVariantId();
              this.handlePossibleWidthChange(true);
          }, 80);
      };

      this._boundGlobalVariantClick = (event) => {
          if (isSyncSuspended()) return;
          if (!event.target.closest('fieldset') && !event.target.closest('variant-selects, variant-radios')) return;
          clearTimeout(this._globalClickTimer);
          this._globalClickTimer = setTimeout(() => {
              if (isSyncSuspended()) return;
              this.currentMainVariantId = this.getCurrentMainVariantId();
              this.handlePossibleWidthChange(true);
          }, 100);
      };

      document.addEventListener('variant:change', this._boundVariantChange);
      document.addEventListener('variant:changed', this._boundVariantChanged);
      document.addEventListener('bl:grouping:swapped', this._boundGroupingSwapped);
      document.addEventListener('click', this._boundGlobalVariantClick);

      this._boundPickerChange = () => {
          if (isSyncSuspended()) return;
          clearTimeout(this._pickerTimer);
          this._pickerTimer = setTimeout(() => {
              if (isSyncSuspended()) return;
              this.currentMainVariantId = this.getCurrentMainVariantId();
              this.handlePossibleWidthChange(true);
          }, 0);
      };

      this.refreshVariantBindings();

      this._bindingRefreshInterval = window.setInterval(() => this.refreshVariantBindings(), 2000);
      this._widthRefreshInterval = window.setInterval(() => this.handlePossibleWidthChange(), 3000);
    }

    destroy() {
      clearTimeout(this._scrollTimer);
      clearTimeout(this._pickerTimer);
      clearTimeout(this._pickerInteractionTimer);
      clearTimeout(this._globalClickTimer);

      if (this._priceFrame) {
        cancelAnimationFrame(this._priceFrame);
      }

      if (this._boundVariantChanged) {
        document.removeEventListener('variant:changed', this._boundVariantChanged);
      }

      if (this._boundVariantChange) {
        document.removeEventListener('variant:change', this._boundVariantChange);
      }

      if (this._boundGroupingSwapped) {
        document.removeEventListener('bl:grouping:swapped', this._boundGroupingSwapped);
      }

      if (this._boundGlobalVariantClick) {
        document.removeEventListener('click', this._boundGlobalVariantClick);
      }

      if (this._variantPicker && this._boundPickerChange) {
        this._variantPicker.removeEventListener('change', this._boundPickerChange);
      }

      if (this._variantPicker && this._boundPickerInteraction) {
        this._variantPicker.removeEventListener('click', this._boundPickerInteraction);
      }

      if (this._pickerObserver) {
        this._pickerObserver.disconnect();
      }

      if (this._bindingRefreshInterval) {
        window.clearInterval(this._bindingRefreshInterval);
      }

      if (this._widthRefreshInterval) {
        window.clearInterval(this._widthRefreshInterval);
      }

      if (this._boundDynamicCheckoutClick) {
        document.removeEventListener('click', this._boundDynamicCheckoutClick, true);
      }

      const registry = getCartAddonInterceptorRegistry();
      registry.providers.delete(this._fetchProviderId);
    }

    refreshVariantBindings() {
        const nextPicker = this.getVariantPicker();
        if (nextPicker === this._variantPicker) return;

        if (this._variantPicker && this._boundPickerChange) {
            this._variantPicker.removeEventListener('change', this._boundPickerChange);
        }

        if (this._variantPicker && this._boundPickerInteraction) {
            this._variantPicker.removeEventListener('click', this._boundPickerInteraction);
        }

        if (this._pickerObserver) {
            this._pickerObserver.disconnect();
            this._pickerObserver = null;
        }

        this._variantPicker = nextPicker;
        if (!this._variantPicker) return;

        this._variantPicker.addEventListener('change', this._boundPickerChange);
        this._variantPicker.addEventListener('click', this._boundPickerInteraction);

        this._pickerObserver = new MutationObserver(() => {
            if (this._suspendSyncUntil && Date.now() < this._suspendSyncUntil) return;
            this.currentMainVariantId = this.getCurrentMainVariantId();
            this.handlePossibleWidthChange(true);
        });

        this._pickerObserver.observe(this._variantPicker, {
            attributes: true,
            childList: true,
            subtree: true
        });
    }

    handlePossibleWidthChange(force = false) {
        const newWidth = this.detectWidthFromDOM();
        if (newWidth && (newWidth !== this.currentWidth || force)) {
            this.updateState(newWidth);
            return;
        }

        const selectedVariant = this.getSelectedMainVariant();
        const variantWidth = this.extractWidthFromVariant(selectedVariant);
        if (variantWidth && (variantWidth !== this.currentWidth || force)) {
            this.updateState(variantWidth);
            return;
        }

        this.scheduleGlobalPriceUpdate();
    }

    getMainProductRoot() {
        return document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
               document.querySelector(`product-info[data-original-section="${this.sectionId}"]`) ||
               document.querySelector('product-info[id^="MainProduct-"]') ||
               document.querySelector('product-info');
    }

    getSelectedMainVariant() {
        const root = this.getMainProductRoot();
        const variantScript = root?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
        if (!variantScript) return null;

        try {
            return JSON.parse(variantScript.textContent);
        } catch (error) {
            return null;
        }
    }

    getVariantPicker() {
        const root = this.getMainProductRoot();
        return root?.querySelector(`variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`) ||
               root?.querySelector('variant-selects, variant-radios') ||
               document.querySelector(`variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`) ||
               document.querySelector('variant-selects, variant-radios');
    }

    normalizeOptionValue(value) {
        return (value || '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();
    }

    extractNumericToken(value) {
        return this.normalizeOptionValue(value).replace(/[^\d]/g, '');
    }

    looksLikeSizeValue(value) {
        const normalized = this.normalizeOptionValue(value);
        const comparable = normalized
            .replace(/\s+/g, '')
            .replace(/[.,/x×-]/g, '')
            .replace(/"/g, '')
            .replace(/'/g, '')
            .replace(/inch|in|mm|cm|2s/g, '');
        const numeric = this.extractNumericToken(value);

        if (!normalized) return false;
        if (/^\d+([.,]\d+)?$/.test(normalized)) return true;
        if (/^\d+([.,]\d+)?\s*2s$/.test(normalized)) return true;
        if (/\d\s*(cm|mm)\b/.test(normalized)) return true;
        if (/^\d+([.,]\d+)?\s*(x|×|-|\/)\s*\d+([.,]\d+)?/.test(normalized)) return true;

        return comparable.length >= 2 && numeric.length >= 2 && !/[a-z]/.test(comparable);
    }

    getSelectedValueFromOptionBlock(block) {
        if (!block) return '';

        const selectedSpan = block.querySelector('[data-selected-value]');
        if (selectedSpan?.textContent?.trim()) return selectedSpan.textContent.trim();

        const checked = block.querySelector('input[type="radio"]:checked');
        if (checked?.value?.trim()) return checked.value.trim();

        const select = block.querySelector('select');
        if (select?.value?.trim()) return select.value.trim();

        const active = block.querySelector('.is-active, .selected, [aria-checked="true"]');
        return (active?.dataset?.value || active?.dataset?.optionValue || active?.textContent || '').trim();
    }

    getOptionValuesFromBlock(block) {
        if (!block) return [];

        const values = new Set();

        block.querySelectorAll('input[type="radio"]').forEach((input) => {
            if (input.value?.trim()) values.add(input.value.trim());
        });

        block.querySelectorAll('select option').forEach((option) => {
            if (option.value?.trim()) values.add(option.value.trim());
            const optionText = option.textContent?.trim();
            if (optionText) values.add(optionText);
        });

        block.querySelectorAll('[data-value], [data-option-value]').forEach((node) => {
            const optionValue = node.dataset?.value || node.dataset?.optionValue || '';
            if (optionValue.trim()) values.add(optionValue.trim());
        });

        const selectedValue = this.getSelectedValueFromOptionBlock(block);
        if (selectedValue) values.add(selectedValue);

        return Array.from(values);
    }

    blockLooksLikeSizeOption(block) {
        const values = this.getOptionValuesFromBlock(block).filter(Boolean);
        if (!values.length) return false;

        const sizeLikeValues = values.filter((value) => this.looksLikeSizeValue(value));
        if (sizeLikeValues.length > 1) return true;
        if (sizeLikeValues.length > 0 && sizeLikeValues.length === values.length) return true;

        return this.looksLikeSizeValue(this.getSelectedValueFromOptionBlock(block));
    }

    detectWidthFromDOM() {
        const picker = this.getVariantPicker();
        
        if (!picker) return null;

        const allBlocks = picker.querySelectorAll('fieldset, .product-form__input, .variant-picker__option');
        for (let block of allBlocks) {
            if (!this.blockLooksLikeSizeOption(block)) continue;

            const selectedValue = this.getSelectedValueFromOptionBlock(block);
            if (selectedValue) return selectedValue;
        }

        const activeEls = picker.querySelectorAll('.is-active, .selected, [aria-checked="true"], input:checked');
        for (let el of activeEls) {
            const val = (el.value || el.dataset.value || el.textContent || '').trim();
            if (this.looksLikeSizeValue(val)) return val;
        }

        return null;
    }

    extractWidthFromVariant(variant) {
      if (!variant || !variant.options) return null;
      for (let val of variant.options) {
          if (this.looksLikeSizeValue(val)) return val;
      }
      return variant.options[0];
    }

    getCurrentMainVariantId() {
        const selectedVariant = this.getSelectedMainVariant();
        if (selectedVariant?.id) return selectedVariant.id;

        const root = this.getMainProductRoot();
        const form = root?.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
                     root?.querySelector('form[action*="/cart/add"]') ||
                     document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`) ||
                     document.querySelector('form[action*="/cart/add"]');
        return form?.querySelector('[name="id"]')?.value || null;
    }

    getSelectedValue() {
      const checked = this.container.querySelector('.washbasin-selector__input:checked');
      if (checked) return checked.value;

      const activeItem = this.container.querySelector('.washbasin-selector__item.is-active');
      if (!activeItem) return null;

      return activeItem.hasAttribute('data-washbasin-none') ? 'none' : activeItem.dataset.washbasinId;
    }

    getVisibleItems() {
      return Array.from(this.items).filter(item =>
        !item.hasAttribute('data-washbasin-none') && item.style.display !== 'none'
      );
    }

    scheduleGlobalPriceUpdate() {
      if (this._priceFrame) cancelAnimationFrame(this._priceFrame);
      this._priceFrame = requestAnimationFrame(() => {
        this._priceFrame = null;
        this.updateGlobalPrice();
      });
    }

    publishStickyBarState(detail) {
      const stickyDetail = Object.assign(
        {
          sectionId: this.sectionId,
          productId: this.config.productId || null
        },
        detail
      );

      window.__blWashbasinV2StickyState = window.__blWashbasinV2StickyState || {};
      const cacheKey = String(stickyDetail.sectionId || stickyDetail.productId || 'default');
      window.__blWashbasinV2StickyState[cacheKey] = stickyDetail;

      document.dispatchEvent(
        new CustomEvent('bl:washbasin-v2:sticky-update', {
          bubbles: true,
          detail: stickyDetail
        })
      );
    }

    getStickyBasinName(activeItem) {
      if (!activeItem) return '';

      const basinId = activeItem.dataset.washbasinId;
      const activePill = basinId
        ? this.container.querySelector(`[data-pill-value="${basinId}"]`)
        : null;
      const selectedLabel = this.container.querySelector('[data-washbasin-selected-value]');

      if (activePill?.textContent?.trim()) {
        return activePill.textContent.trim();
      }

      if (selectedLabel?.textContent?.trim()) {
        return selectedLabel.textContent.trim();
      }

      return activeItem.dataset.washbasinName || '';
    }

    ensureValidSelection(preferredValue = null, silent = true) {
      const currentValue = preferredValue || this.getSelectedValue() || this.lastSelectedBasinId;

      if (currentValue === 'none') {
        this.selectValue('none', true);
        return 'none';
      }

      if (currentValue) {
        const currentItem = this.container.querySelector(`[data-washbasin-id="${currentValue}"]`);
        const currentMatch = currentItem ? this.getMatchForWidth(currentItem, this.currentWidth) : { variantId: null };

        if (currentItem && currentItem.style.display !== 'none' && currentMatch.variantId) {
          this.selectValue(currentValue, silent);
          return currentValue;
        }
      }

      const visibleItems = this.getVisibleItems();
      if (!visibleItems.length) {
        this.selectValue('none', true);
        return 'none';
      }

      const rememberedBasinId = this.lastSelectedBasinId || this.loadRememberedBasin();
      if (rememberedBasinId) {
        const rememberedItem = this.container.querySelector(`[data-washbasin-id="${rememberedBasinId}"]`);
        const rememberedMatch = rememberedItem ? this.getMatchForWidth(rememberedItem, this.currentWidth) : { variantId: null };

        if (rememberedItem && rememberedItem.style.display !== 'none' && rememberedMatch.variantId) {
          this.selectValue(rememberedBasinId, silent);
          return rememberedBasinId;
        }
      }

      this.selectValue('none', true);
      return 'none';
    }

    selectValue(val, silent = false, persist = !silent) {
      const input = this.container.querySelector(`input[value="${val}"]`);
      if (input) {
        input.checked = true;

        if (val && val !== 'none') {
          this.lastSelectedBasinId = val;
          if (persist) {
            this.rememberBasinSelection(val);
          }
        } else if (persist) {
          this.lastSelectedBasinId = null;
          this.rememberBasinSelection(null);
        }
        
        // Update is-active classes
        this.items.forEach(item => item.classList.remove('is-active'));
        const selectedItem = input.closest('.washbasin-selector__item');
        if (selectedItem) selectedItem.classList.add('is-active');

        this.syncPillToValue(val);
        this.scrollToValue(val, silent);
        if (!silent) this.scheduleGlobalPriceUpdate();
      }
    }

    syncPillToValue(val) {
      let selectedName = '';
      this.pills.forEach(p => {
        const isActive = p.dataset.pillValue === val;
        p.classList.toggle('active', isActive);
        if (isActive) selectedName = p.textContent.trim();
      });
      
      this.pillRow.classList.toggle('has-selection', val !== 'none');
      
      const labelEl = this.container.querySelector('[data-washbasin-selected-value]');
      if (labelEl) {
        labelEl.textContent = selectedName;
      }

      const expandable = this.container.querySelector('[data-washbasin-expandable]');
      if (expandable) {
        expandable.style.display = '';
      }
    }

    scrollToValue(val, silent = false) {
      if (!this.slider || val === 'none') return;
      if (silent && this._suspendSyncUntil && Date.now() < this._suspendSyncUntil) return;
      const item = this.container.querySelector(`[data-washbasin-id="${val}"]`);
      if (item) {
        const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
        const centeredLeft = item.offsetLeft - ((this.slider.clientWidth - item.offsetWidth) / 2);
        const targetLeft = Math.min(maxScroll, Math.max(0, centeredLeft));

        this._isInternalScroll = true;
        this.slider.scrollTo({
          left: targetLeft,
          behavior: silent ? 'auto' : 'smooth'
        });
        setTimeout(() => { this._isInternalScroll = false; }, 600);
      }
    }

    syncPillToVisibleCard() {
      const currentValue = this.getSelectedValue();
      if (!currentValue || currentValue === 'none') return;

      const visibleItems = this.getVisibleItems();
      if (!visibleItems.length) return;

      const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      const edgeThreshold = 24;
      let closestItem = null;

      if (this.slider.scrollLeft <= edgeThreshold) {
        closestItem = visibleItems[0];
      } else if (maxScroll - this.slider.scrollLeft <= edgeThreshold) {
        closestItem = visibleItems[visibleItems.length - 1];
      } else {
        const sliderRect = this.slider.getBoundingClientRect();
        const center = sliderRect.left + (sliderRect.width / 2);
        let minDistance = Infinity;

        visibleItems.forEach(item => {
          const rect = item.getBoundingClientRect();
          const itemCenter = rect.left + (rect.width / 2);
          const dist = Math.abs(center - itemCenter);
          if (dist < minDistance) {
            minDistance = dist;
            closestItem = item;
          }
        });
      }

      if (closestItem) {
        const val = closestItem.dataset.washbasinId;
        const input = closestItem.querySelector('input');
        if (input && !input.checked) {
          input.checked = true;
          
          this.items.forEach(item => item.classList.remove('is-active'));
          closestItem.classList.add('is-active');

          this.syncPillToValue(val);
          this.scheduleGlobalPriceUpdate();
        }
      }
    }

    scrollSlider(dir) {
      const visibleItems = this.getVisibleItems();
      if (!visibleItems.length) return;

      const currentValue = this.getSelectedValue();
      let currentIndex = visibleItems.findIndex(item => item.dataset.washbasinId === currentValue);

      if (currentIndex < 0) {
        const sliderRect = this.slider.getBoundingClientRect();
        const center = sliderRect.left + (sliderRect.width / 2);
        let minDistance = Infinity;

        visibleItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const itemCenter = rect.left + (rect.width / 2);
          const dist = Math.abs(center - itemCenter);
          if (dist < minDistance) {
            minDistance = dist;
            currentIndex = index;
          }
        });
      }

      if (currentIndex < 0) currentIndex = 0;

      const nextIndex = dir === 'next'
        ? Math.min(visibleItems.length - 1, currentIndex + 1)
        : Math.max(0, currentIndex - 1);

      const nextItem = visibleItems[nextIndex];
      if (!nextItem) return;

      this.selectValue(nextItem.dataset.washbasinId);
    }

    updateProgressBar() {
      if (!this.slider) return;
      const progressEl = this.container.querySelector('.washbasin-selector__progress');
      if (!progressEl) return;
      
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      if (maxScroll <= 0) {
        progressEl.style.width = '100%';
        return;
      }
      
      const scrollRatio = this.slider.scrollLeft / maxScroll;
      const percent = Math.max(10, Math.min(100, scrollRatio * 100)); // min 10% visible
      // Because we scroll snap, we want the bar to feel full when reaching the end
      progressEl.style.width = `${percent}%`;
    }

    // --- Price & State Logic (Re-used from V1) ---

    updateState(width) {
      const previousValue = this.getSelectedValue();
      this.currentWidth = width;
      this.items.forEach(item => {
        if (item.hasAttribute('data-washbasin-none')) return;
        const match = this.getMatchForWidth(item, width);
        item.style.display = match.variantId ? '' : 'none';
        
        // Hide corresponding pill if model not available for this width
        const pill = this.container.querySelector(`[data-pill-value="${item.dataset.washbasinId}"]`);
        if (pill) pill.style.display = match.variantId ? '' : 'none';

        if (match.variantId) {
          const pDisp = item.querySelector('[data-washbasin-price-display]');
          if (pDisp) {
             pDisp.textContent = `+${this.formatMoney(match.price)}`;
             pDisp.style.display = '';
          }
        } else {
          const pDisp = item.querySelector('[data-washbasin-price-display]');
          if (pDisp) {
            pDisp.textContent = '';
            pDisp.style.display = 'none';
          }
        }
      });
      this.ensureValidSelection(previousValue, true);
      this.updateProgressBar();
      this.scheduleGlobalPriceUpdate();
    }

    getMatchForWidth(item, targetWidth) {
        if (!targetWidth) return { variantId: null, price: 0 };
        const mapping = JSON.parse(item.dataset.mapping || '{}');
        const prices = JSON.parse(item.dataset.prices || '{}');
        
        let variantId = mapping[targetWidth];
        let price = prices[targetWidth];

        if (!variantId) {
            const cleanTarget = targetWidth.toLowerCase().replace(/[^a-z0-9]/g, '');
            const targetNum = targetWidth.replace(/[^\d]/g, '');
            
            for (let key in mapping) {
                const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
                const keyNum = key.replace(/[^\d]/g, '');
                if (cleanKey === cleanTarget || (targetNum && keyNum === targetNum)) {
                    variantId = mapping[key];
                    price = prices[key];
                    break;
                }
            }
        }
        return { variantId, price };
    }

    formatMoney(cents) {
        if (window.Shopify && window.Shopify.formatMoney) {
            const format =
                window.FoxTheme?.settings?.moneyFormat
                || document.querySelector('[data-money-format]')?.getAttribute('data-money-format')
                || document.querySelector('[data-money-format]')?.content
                || "${{amount}}";
            return window.Shopify.formatMoney(cents, format);
        }
        return (cents / 100).toLocaleString('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).replace(',00', '');
    }

    getBaseCompareAtPrice(baseVariantId = this.currentMainVariantId) {
        if (baseVariantId && this.config.variantCompareAtPrices && this.config.variantCompareAtPrices[baseVariantId]) {
            return parseInt(this.config.variantCompareAtPrices[baseVariantId], 10) || 0;
        }

        const selectedVariant = this.getSelectedMainVariant();
        return parseInt(selectedVariant?.compare_at_price || 0, 10) || 0;
    }

    buildBundlePriceState(basePrice, addonPrice) {
        const total = basePrice + addonPrice;
        const displayPrice = this.formatMoney(total);
        const baseCompareAtPrice = this.getBaseCompareAtPrice();
        const hasDiscount = baseCompareAtPrice > basePrice && basePrice > 0 && addonPrice > 0;
        let compareAtTotal = 0;

        if (hasDiscount) {
            compareAtTotal = Math.round((total * baseCompareAtPrice) / basePrice);
        }

        return {
            total,
            displayPrice,
            compareAtTotal,
            compareAtPrice: compareAtTotal > total ? this.formatMoney(compareAtTotal) : '',
            isOnSale: compareAtTotal > total
        };
    }

    getWidthForMainVariantId(mainVariantId) {
        if (!mainVariantId || !this.config?.variantWidths) return null;
        return this.config.variantWidths[String(mainVariantId)] || null;
    }

    resolveCurrentBundleWidth(mainVariantId = null) {
        const configuredWidth = this.getWidthForMainVariantId(mainVariantId);
        if (configuredWidth) return configuredWidth;

        const selectedVariant = this.getSelectedMainVariant();
        const selectedVariantWidth = selectedVariant
            ? this.getWidthForMainVariantId(selectedVariant.id) || this.extractWidthFromVariant(selectedVariant)
            : null;
        if (selectedVariantWidth) return selectedVariantWidth;

        const domWidth = this.detectWidthFromDOM();
        if (domWidth) return domWidth;

        return this.currentWidth || null;
    }

    getPriceTargets() {
        const targets = [];
        const mainPrice = document.querySelector(`#price-${this.sectionId}`);
        if (mainPrice) targets.push(mainPrice);

        document.querySelectorAll('#shopify-section-sticky-atc-bar sticky-atc-bar .sticky-atc-bar__product-info').forEach(target => {
            if (target.querySelector('.f-price')) targets.push(target);
        });

        return targets;
    }

    getButtonTargets() {
        const targets = [];
        const seen = new Set();

        const addIfPresent = (button) => {
            if (!button || seen.has(button)) return;
            seen.add(button);
            targets.push(button);
        };

        addIfPresent(document.querySelector(`#ProductSubmitButton-${this.sectionId}`));

        const mainForm = document.querySelector(`form[action*="/cart/add"][id*="${this.sectionId}"]`);
        addIfPresent(mainForm?.querySelector('.product-form__submit'));

        document.querySelectorAll('#shopify-section-sticky-atc-bar .product-form__submit').forEach(addIfPresent);

        return targets;
    }

    getSelectedProductAddons() {
        const store = getProductAddonStateStore();
        const selection =
            store.selections.get(String(this.sectionId || ''))
            || store.selections.get(String(this.config.productId || ''))
            || null;

        return selection || {
            hasAddons: false,
            selections: [],
            addonVariantIds: [],
            addonNames: [],
            addonName: '',
            addonImage: '',
            addonPrice: 0,
            addonCompareAtPrice: 0
        };
    }

    updateGlobalPrice() {
        if (this._isUpdating) return;
        this._isUpdating = true;

        try {
            this.currentMainVariantId = this.getCurrentMainVariantId() || this.currentMainVariantId;
            const resolvedWidth = this.resolveCurrentBundleWidth(this.currentMainVariantId);
            if (resolvedWidth) this.currentWidth = resolvedWidth;
            const activeItem = this.container.querySelector('.washbasin-selector__item.is-active:not([data-washbasin-none])');
            let washbasinPrice = 0;
            let matchedVariantId = null;
            const addonDetail = this.getSelectedProductAddons();
            const addonPrice = parseInt(addonDetail.addonPrice || 0, 10) || 0;
            
            if (activeItem) {
                const match = this.getMatchForWidth(activeItem, resolvedWidth || this.currentWidth);
                washbasinPrice = parseInt(match.price || 0);
                matchedVariantId = match.variantId || null;
            }
            
            let baseVariantId = this.currentMainVariantId;

            let currentBasePrice = 0;
            if (baseVariantId && this.config.variantPrices && this.config.variantPrices[baseVariantId]) {
                currentBasePrice = parseInt(this.config.variantPrices[baseVariantId], 10);
            }
            
            const linkedPriceTotal = washbasinPrice + addonPrice;
            const bundlePriceState = this.buildBundlePriceState(currentBasePrice, linkedPriceTotal);
            const total = bundlePriceState.total;
            const formattedTotal = bundlePriceState.displayPrice;
            const selectedVariant = this.getSelectedMainVariant();
            const basinName = this.getStickyBasinName(activeItem);
            const stickyDetail = {
                hasBasin: Boolean(activeItem && matchedVariantId),
                basinName,
                basinImage: activeItem?.dataset.washbasinImage || '',
                basinVariantId: matchedVariantId,
                basinPrice: washbasinPrice,
                hasAddons: Boolean(addonDetail.hasAddons),
                addonName: addonDetail.addonName || '',
                addonNames: addonDetail.addonNames || [],
                addonImage: addonDetail.addonImage || '',
                addonVariantIds: addonDetail.addonVariantIds || [],
                addonPrice,
                mainVariantId: this.currentMainVariantId || null,
                mainVariantSize: resolvedWidth || this.currentWidth || this.extractWidthFromVariant(selectedVariant) || '',
                bundleTotal: total,
                bundleTotalFormatted: formattedTotal,
                bundleCompareAt: bundlePriceState.compareAtTotal,
                bundleCompareAtFormatted: bundlePriceState.compareAtPrice,
                bundleIsOnSale: bundlePriceState.isOnSale
            };
            
            this.getPriceTargets().forEach(target => {
                window.BLBundlePriceState?.applyPriceNode?.(target, bundlePriceState, { lock: false });
            });

            let priceForButton;
            if (linkedPriceTotal > 0) {
                priceForButton = formattedTotal;
            } else {
                const nativePriceSection = document.querySelector(`#price-${this.sectionId}`);
                const nativePriceEl = nativePriceSection
                    ? nativePriceSection.querySelector('.f-price-item--regular, .price-item--regular, .f-price__regular .money')
                    : null;
                priceForButton = nativePriceEl ? nativePriceEl.textContent.trim() : null;
            }

            if (linkedPriceTotal > 0) {
                window.BLAtcPriceSync?.applyBundlePrice('washbasin-v2', formattedTotal);
            } else {
                window.BLAtcPriceSync?.clearBundlePrice('washbasin-v2');
            }

            window.BLBundlePriceState?.setOwnerState?.('washbasin-v2', linkedPriceTotal > 0, {
                buttonReady: linkedPriceTotal > 0 && Boolean(priceForButton)
            });

            if (priceForButton) {
                this.getButtonTargets().forEach(btn => {
                    const btnTextSpan = btn.querySelector('.btn__text');
                    if (btnTextSpan) {
                        let wrapper = btn.querySelector('[data-btn-price-wrapper]');
                        if (!wrapper) {
                            wrapper = document.createElement('span');
                            wrapper.setAttribute('data-btn-price-wrapper', '');
                            wrapper.className = 'btn__price-arrow-wrapper';
                            wrapper.innerHTML = `
                                <span class="btn__price-separator">|</span>
                                <span data-btn-price-display></span>
                                <svg class="btn__price-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                            `;
                            btnTextSpan.parentNode.insertBefore(wrapper, btnTextSpan.nextSibling);
                        }
                        wrapper.dataset.btnPriceSource = linkedPriceTotal > 0 ? 'bundle' : 'default';
                        const priceDisplay = wrapper.querySelector('[data-btn-price-display]');
                        if (priceDisplay) {
                            priceDisplay.textContent = priceForButton;
                        }
                    }
                });
            }

            this.publishStickyBarState(stickyDetail);
        } finally {
            requestAnimationFrame(() => { this._isUpdating = false; });
        }
    }

    getSelectedVariantId(mainVariantId = null) {
        const activeItem = this.container.querySelector(`.washbasin-selector__item.is-active:not([data-washbasin-none])`);
        if (!activeItem) return null;
        const resolvedWidth = this.resolveCurrentBundleWidth(mainVariantId);
        if (resolvedWidth) this.currentWidth = resolvedWidth;
        const match = this.getMatchForWidth(activeItem, resolvedWidth || this.currentWidth);
        return match.variantId;
    }

    buildBundlePayload(config) {
        if (!this.container.isConnected) return null;

        const addonDetail = this.getSelectedProductAddons();
        const addonVariantIds = Array.isArray(addonDetail.addonVariantIds)
            ? addonDetail.addonVariantIds.filter(Boolean)
            : [];

        let items = [];
        let sections = '';
        let bundleId = null;

        if (config.body instanceof FormData) {
            const formData = config.body;
            const mainId = formData.get('id');
            const washbasinVariantId = this.getSelectedVariantId(mainId);
            if (!washbasinVariantId && !addonVariantIds.length) return null;
            const mainQty = formData.get('quantity') || 1;
            sections = formData.get('sections') || formData.get('sections_url') || '';
            bundleId = buildStableBundleId(mainId, [washbasinVariantId, ...addonVariantIds].filter(Boolean));
            
            const mainProps = {
                '_msw_bundle_id': bundleId,
                '_linkedTo': [washbasinVariantId, ...addonVariantIds].filter(Boolean).join(',')
            };
            for (let [key, value] of formData.entries()) {
                if (key.startsWith('properties[')) {
                    const propName = key.match(/properties\[(.*?)\]/)[1];
                    mainProps[propName] = value;
                }
            }

            items = [{ id: parseInt(mainId, 10), quantity: parseInt(mainQty, 10), properties: mainProps }];

            if (washbasinVariantId) {
                items.push({
                    id: parseInt(washbasinVariantId, 10),
                    quantity: 1,
                    properties: { '_msw_bundle_id': bundleId, '_is_washbasin': 'true', '_linkedTo': mainId }
                });
            }

            addonVariantIds.forEach((variantId) => {
                items.push({
                    id: parseInt(variantId, 10),
                    quantity: 1,
                    properties: { '_msw_bundle_id': bundleId, '_isAddon': 'true', '_linkedTo': mainId }
                });
            });
        } else if (typeof config.body === 'string') {
            try {
                const body = JSON.parse(config.body);
                const mainId = body.id || (body.items && body.items[0]?.id);
                const washbasinVariantId = this.getSelectedVariantId(mainId);
                if (!washbasinVariantId && !addonVariantIds.length) return null;
                const mainQty = body.quantity || (body.items && body.items[0]?.quantity) || 1;
                sections = body.sections || body.sections_url || '';
                bundleId = buildStableBundleId(mainId, [washbasinVariantId, ...addonVariantIds].filter(Boolean));
                
                const mainProps = Object.assign({}, (body.properties || (body.items && body.items[0]?.properties) || {}));
                mainProps['_msw_bundle_id'] = bundleId;
                mainProps['_linkedTo'] = [washbasinVariantId, ...addonVariantIds].filter(Boolean).join(',');

                items = [{ id: parseInt(mainId, 10), quantity: parseInt(mainQty, 10), properties: mainProps }];

                if (washbasinVariantId) {
                    items.push({
                        id: parseInt(washbasinVariantId, 10),
                        quantity: 1,
                        properties: { '_msw_bundle_id': bundleId, '_is_washbasin': 'true', '_linkedTo': mainId }
                    });
                }

                addonVariantIds.forEach((variantId) => {
                    items.push({
                        id: parseInt(variantId, 10),
                        quantity: 1,
                        properties: { '_msw_bundle_id': bundleId, '_isAddon': 'true', '_linkedTo': mainId }
                    });
                });
            } catch(e) {}
        }

        if (!items.length) return null;

        return { items, sections };
    }

    interceptFetch() {
        const registry = installCartAddonFetchInterceptor();

        registry.providers.set(this._fetchProviderId, {
            priority: 20,
            getBundlePayload: (config) => this.buildBundlePayload(config),
            onSuccess: () => {
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
            }
        });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-washbasin-selector-v2]').forEach(el => {
      if (!el.hasAttribute('data-initialized')) {
        el.setAttribute('data-initialized', 'true');
        // Store instance reference so BL-product-grouping.js can destroy
        // the previous selector cleanly before each AJAX swap.
        el.__blWbInstance = new WashbasinSelectorV2(el);
      }
    });
  };

  init();
  document.addEventListener('shopify:section:load', init);
})();
